import {
  createEnvironmentInjector,
  effect,
  EnvironmentInjector,
  inject,
  Injectable,
  runInInjectionContext,
  signal,
  untracked,
} from '@angular/core';
import { OpenCasesService, VideoHubService } from '@core/services';
import { CaseSession } from './case-session';

/**
 * The sessions of the cases held open, one per tab. A session opens with the first screen that
 * needs it and closes with its tab, so a case is fetched once however often its tab is shown.
 */
@Injectable({ providedIn: 'root' })
export class CaseSessionsService {
  private readonly parent = inject(EnvironmentInjector);
  private readonly openCases = inject(OpenCasesService);
  private readonly held = new Map<string, { session: CaseSession; scope: EnvironmentInjector }>();
  private readonly list = signal<readonly CaseSession[]>([]);

  /** Every session held, for a screen to follow their state, their loading first. */
  readonly all = this.list.asReadonly();

  constructor() {
    const hub = inject(VideoHubService);
    hub.onVideoUpdated$.subscribe((video) => this.get(video.id)?.applyLiveUpdate(video));
    hub.onVideoCutProgress$.subscribe((cut) => this.get(cut.videoId)?.updateVideoCuts(cut));
    hub.onVideoDeleted$.subscribe((video) => this.openCases.close(video.id));

    effect(() => {
      const listed = new Set(this.openCases.opened().map((held) => held.id));
      for (const id of this.held.keys()) {
        if (!listed.has(id)) {
          this.drop(id);
        }
      }
    });
  }

  get(id: string): CaseSession | undefined {
    return this.held.get(id)?.session;
  }

  /** The session a chat belongs to: a guest's chat goes by the case's shared id. */
  forChat(chatId: string): CaseSession | undefined {
    return (
      this.get(chatId) ??
      [...this.held.values()].find(({ session }) => session.video()?.sharedId === chatId)?.session
    );
  }

  /** The session of a case, opened if it is not held yet. */
  open(id: string): CaseSession {
    const held = this.held.get(id);
    if (held) {
      return held.session;
    }
    // Its own injector: what the session fetches and watches ends when its tab closes. Untracked,
    // as a screen asks for it from a computed, where no effect may be created.
    const scope = createEnvironmentInjector([], this.parent);
    const session = untracked(() => runInInjectionContext(scope, () => new CaseSession(id)));
    this.held.set(id, { session, scope });
    untracked(() => this.list.update((sessions) => [...sessions, session]));
    // Listed at once, or the sweep below would take it for a closed tab before it names itself.
    untracked(() => this.openCases.hold(id));
    return session;
  }

  private drop(id: string): void {
    this.held.get(id)?.scope.destroy();
    this.held.delete(id);
    this.list.update((sessions) => sessions.filter((session) => session.id !== id));
  }
}
